// Typed client for the Unitly Worker API. Types come from packages/contracts.
import type {
  Job, LayerInventory, LayerMapping, ParameterSet, PlanPreview, Project, ProjectModel, StartUploadResponse,
} from "@unitly/contracts";

export const API_URL: string =
  import.meta.env.VITE_API_URL ??
  (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) ? "http://127.0.0.1:8787" : "https://api.unitly.8nomads.com");

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: unknown) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(data?.error ?? `Request failed (${res.status})`, res.status, data?.details);
  return data as T;
}

export type ProjectWithJobs = Project & { jobs: Job[] };
export type ParametersResponse = { parameters: ParameterSet; saved: boolean; updated_at: string | null };

export const api = {
  listProjects: () => call<{ projects: Project[] }>("GET", "/api/projects").then((r) => r.projects),
  createProject: (name: string) => call<Project>("POST", "/api/projects", { name }),
  getProject: (id: string) => call<ProjectWithJobs>("GET", `/api/projects/${id}`),
  renameProject: (id: string, name: string) => call<Project>("PATCH", `/api/projects/${id}`, { name }),
  startUpload: (id: string, file_name: string, size_bytes: number) =>
    call<StartUploadResponse>("POST", `/api/projects/${id}/uploads`, { file_name, size_bytes }),
  completeUpload: (id: string, uploadId: string, parts: { part_number: number; etag: string }[]) =>
    call<Project>("POST", `/api/projects/${id}/uploads/${encodeURIComponent(uploadId)}/complete`, { parts }),
  abortUpload: (id: string, uploadId: string) =>
    fetch(`${API_URL}/api/projects/${id}/uploads/${encodeURIComponent(uploadId)}`, { method: "DELETE" }),
  startJob: (id: string, kind: "inventory" | "model") => call<Job>("POST", `/api/projects/${id}/jobs`, { kind }),
  getJob: (jobId: string) => call<Job>("GET", `/api/jobs/${jobId}`),
  inventory: (id: string) => call<LayerInventory>("GET", `/api/projects/${id}/artifacts/inventory`),
  preview: (id: string) => call<PlanPreview>("GET", `/api/projects/${id}/artifacts/preview`),
  model: (id: string) => call<ProjectModel>("GET", `/api/projects/${id}/artifacts/model`),
  saveMapping: (id: string, mapping: LayerMapping) => call<Project>("PUT", `/api/projects/${id}/mapping`, mapping),
  parameters: (id: string) => call<ParametersResponse>("GET", `/api/projects/${id}/parameters`),
  saveParameters: (id: string, p: ParameterSet) => call<ParametersResponse>("PUT", `/api/projects/${id}/parameters`, p),
};

/** Poll a job until it is done or failed. */
export async function waitForJob(job: Job, onUpdate: (j: Job) => void, signal?: AbortSignal): Promise<Job> {
  let j = job;
  let delay = 800;
  while (j.status === "queued" || j.status === "running") {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(3000, delay * 1.25);
    j = await api.getJob(j.id);
    onUpdate(j);
  }
  return j;
}
